import { Checkbox, Select, Stack } from 'components/ui';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { SpecialistTypes } from 'utils/queries';

export type Filter = {
  location: Sanity.Schema.Location['_id'];
  specialistType: Sanity.Schema.SpecialistType['_id'];
  services: Sanity.Schema.Service['_id'][];
};

export interface ServicesFilterProps {
  locations: Sanity.Schema.Location[];
  specialistTypes: SpecialistTypes;
  onChange?: (filter: Filter) => void;
}

const ServicesFilter: FC<ServicesFilterProps> = ({
  locations,
  specialistTypes,
  onChange,
}) => {
  const { register, handleSubmit, watch } = useFormContext<Filter>();

  // Watch the specialist type field
  const selectedSpecialistType = watch('specialistType');

  // Format specialist type data to return an array of specialist types with all the related services for that specialist type
  const formattedSpecialistTypes = specialistTypes?.map((specialistType) => ({
    _id: specialistType._id,
    name: specialistType.name,
    services: (
      specialistType.relatedSpecialists
        .flatMap((specialist) => specialist.services?.map((service) => service))
        // Filter out duplicate entries
        .filter((v, i, a) => a.findIndex((t) => t?._id === v?._id) === i) || []
    )
      // Sort alphabetically
      .sort((a, b) => `${a?.name}`.localeCompare(`${b?.name}`)),
  }));

  // Services that fall under the passed in specialist type
  const getSelectedSpecialistTypeServices = (specialistType: string) =>
    formattedSpecialistTypes.find((type) => type._id === specialistType)
      ?.services;

  // Handle submission
  const onSubmit = (data: Filter) => {
    // Return ALL services when specialist type is selected but no services are selected
    if (data.specialistType !== 'all' && data.services.length === 0) {
      const services =
        getSelectedSpecialistTypeServices(data.specialistType)?.map(
          (service) => service?._id || ''
        ) || [];

      onChange &&
        onChange({
          ...data,
          services,
        });
    } else {
      // Default return all formdata as active filter
      onChange && onChange(data);
    }
  };

  const handleChange = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <form onChange={handleChange}>
        <Stack space={3}>
          <Select
            id='location'
            label='Select location'
            {...register('location')}
          >
            <option value='all'>All</option>
            {locations
              .filter((location) => !location.primary)
              .map(({ _id, name }) => (
                <option value={_id} key={_id}>
                  {name}
                </option>
              ))}
          </Select>
          <Select
            id='specialistType'
            label='Select Specialist type'
            {...register('specialistType')}
          >
            <option value='all'>All</option>
            {formattedSpecialistTypes.map(({ _id, name }) => (
              <option value={_id} key={_id}>
                {name}
              </option>
            ))}
          </Select>
          {selectedSpecialistType !== 'all' && (
            <div>
              <Stack space={1}>
                {getSelectedSpecialistTypeServices(selectedSpecialistType)?.map(
                  (service) => {
                    if (service && service.name) {
                      return (
                        <Checkbox
                          id={service._id}
                          label={service.name}
                          value={service._id}
                          key={service._id}
                          {...register('services')}
                        />
                      );
                    }

                    return null;
                  }
                )}
              </Stack>
            </div>
          )}
        </Stack>
      </form>
    </>
  );
};

export default ServicesFilter;
